import { AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatInput } from '@angular/material';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { Subject } from 'rxjs';
import { AssessmentService } from 'src/app/core/api/assessment.service';
import { PatientService } from 'src/app/core/api/patient.service';
import { AssessmentFormService } from 'src/app/encounters/assessment-form.service';
import { AssessmentRouteData } from 'src/app/encounters/assessment/assessment.component';
import { MedicalHistoryLinkingService } from 'src/app/encounters/encounter-linking/medical-history/medical-history-linking.service';
import { MedicalHistoryLayoutService } from 'src/app/logged-in-navbar/clinic-setup-modal/layouts/medical-history-layout/medical-history-layout.service';
import { DryEyeMedicalHistoryFormGroup } from '../dry-eye-medical-history-form.model';
import { Assessment, Drug, QuestionnaireRequest } from './../../../../../../API';
import { DerfsService } from './../../../../../questionnaires/derfs/derfs.service';
import { SchemaService } from './../../../../symptoms/services/schema/schema.service';

@Component({
  selector: 'csi-medical-history-fields',
  templateUrl: './medical-history-fields.component.html',
  styleUrls: ['./medical-history-fields.component.css']
})
export class MedicalHistoryFieldsComponent implements AfterViewInit, OnDestroy, OnInit {
  @ViewChild('datePickerInput', { static: false }) datePickerInputRef: MatInput;
  @Input() formGroup: DryEyeMedicalHistoryFormGroup;
  @Input() medicalHistoryApplicableFields: string[];
  @Input() assessmentMethod: string;
  @Input() assessmentRouteData: AssessmentRouteData;
  @Input() onPatientFileSaved$: Subject<Object>;
  @Input() onQuestionnaireLinked$: Subject<QuestionnaireRequest[]>;
  @Input() isMedicationSection: boolean;
  @Input() questionnaireMode: boolean;
  @Input() isLocked: boolean;

  readonly disabledFieldsDisclaimer = `The disabled fields are non-modifiable data. This information is populated based on the response by the patients when they fill out CDERFS/DERFS and Intake Form. You can change the information through the patient profile.`;

  readonly eyeTearingQuantityBottomLabels = [
    'N/A',
    'NO TEARS AT ALL',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    'FALLS ON CHEEK ALL THE TIME'
  ];

  readonly cityHumidityLevelBottomLabels = [
    'VERY DRY',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    'VERY HUMID'
  ];

  readonly cityPollutionLevelBottomLabels = [
    'NO POLLUTION',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    'VERY POLLUTED'
  ];

  readonly sensitivityToLightBottomLabels = ['NONE', '', '', '', '', '', '', '', '', '', 'SEVERE'];
  readonly sensitivityToWindBottomLabels = ['NONE', '', '', '', '', '', '', '', '', '', 'SEVERE'];
  readonly impactOfSymptomsOnMoodBottomLabels = [
    'NONE',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    'SEVERE'
  ];
  readonly impactOfSymptomsOnLifeBottomLabels = [
    'NONE',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    'SEVERE'
  ];

  readonly eyeItchinessQuantityBottomLabels = [
    'NONE',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    'SEVERE'
  ];

  readonly frequencyOfSymptomsBottomLabels = [
    'N/A',
    'RARELY',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    'ALL THE TIME'
  ];

  readonly severityOfSymptomsBottomLabels = [
    'N/A',
    'VERY MILD',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    'VERY SEVERE'
  ];

  readonly qualityBottomLabels = ['N/A', 'POOR', '', '', '', '', '', '', '', '', 'GOOD'];

  readonly stressLevelBottomLabels = ['N/A', 'LOW', '', '', '', '', '', '', '', '', 'HIGH'];

  public id: string;
  public nonModifiableData;
  public disabledNonModifiableFields = new Set();

  // Guards every write path in this form: the "Edit data" affordance that opens the patient
  // file modal, and the last-eye-exam date picker. Both were gated on isLocked alone, so
  // view-only staff could still change them. isViewOnly delegates to canEditAssessment.
  public get canEditMedicalHistory(): boolean {
    return !this.isLocked && !this.assessmentService.isViewOnly;
  }
  public assessment: Assessment;
  public drugsNotCopied: string[] = [];
  public drugsNotFound: string[] = [];
  public dateToday: Date = new Date();

  public ngSelectMedicalHistoryFieldKeys = {
    tearingQuality: 'tearingQuality',
    screenTime: 'screenTime',
    bothersomeEye: 'bothersomeEye',
    symptomSeverityOccurrence: 'symptomSeverityOccurrence',
    sleepingPosition: 'sleepingPosition',
    eyeRubbingFrequency: 'eyeRubbingFrequency',
    sunTanExposureFrequency: 'sunTanExposureFrequency',
    lastSunExposure: 'lastSunExposure',
    eyeColor: 'eyeColor',
    longSunExposure: 'longSunExposure',
    naturalHairColor: 'naturalHairColor',
    darkerAfterSunExposure: 'darkerAfterSunExposure',
    skinColor: 'skinColor',
    faceSunSensitivity: 'faceSunSensitivity',
    freckles: 'freckles',
    tanIntensity: 'tanIntensity',
    wearsGlasses: 'wearsGlasses'
  };

  medicationMap = {};

  constructor(
    public schemaService: SchemaService,
    public medicalHistoryLayoutService: MedicalHistoryLayoutService,
    public patientService: PatientService,
    public assessmentFormService: AssessmentFormService,
    public derfsService: DerfsService,
    private assessmentService: AssessmentService,
    private medicalHistoryLinkingService: MedicalHistoryLinkingService
  ) {
    this.updateAssessmentId();
  }

  ngOnInit() { }

  ngOnDestroy(): void {}

  ngAfterViewInit(): void {
    this.assessment = this.assessmentRouteData.assessment;

    if (this.isMedicationSection) {
      return;
    }

    this.onPatientFileSaved$.pipe(untilDestroyed(this)).subscribe(patientFileSaveData => {
      if (!patientFileSaveData) {
        return;
      }
      this.updateAssessmentId();
      this.assessmentService.getAssessment(this.id).subscribe(updatedAssessment => {
        if (this.id === 'new' || this.isToday(new Date(updatedAssessment.createdAt))) {
          this.medicalHistoryLinkingService.populateFromSources(this.formGroup, [
            { populatorKey: 'nonModifiable', data: patientFileSaveData }
          ]);
          this.disableNonModfiableControls();
        }
      });
    });

    this.onQuestionnaireLinked$.pipe(untilDestroyed(this)).subscribe(linkedQuestionnaires => {
      this.medicalHistoryLinkingService.populateFromLinkedQuestionnaires(
        this.formGroup,
        linkedQuestionnaires
      );
    });

    this.populateInitialMedicalHistory();
  }

  /**
   * Runs once on load. Builds the list of sources that seed a brand-new (or
   * same-day) assessment and applies them through the single orchestrator.
   * Replaces a former `setTimeout(1000)` guess: the only thing the timer waited
   * for was patient/questionnaire data, which `getPatientQuestionnaire()`
   * resolves deterministically.
   */
  private populateInitialMedicalHistory(): void {
    if (this.id === 'new') {
      if (!this.patientService.patient) {
        return;
      }
      this.patientService
        .getPatientQuestionnaire()
        .pipe(untilDestroyed(this))
        .subscribe(() => {
          const latestDerfs = this.medicalHistoryLinkingService.getLatestCompletedDerfs(
            this.patientService.questionnaires
          );
          this.medicalHistoryLinkingService.populateFromSources(this.formGroup, [
            { populatorKey: 'derfs', data: latestDerfs },
            { populatorKey: 'intakeForm', data: this.patientService.intakeForms[0] },
            { populatorKey: 'nonModifiable', data: this.getPatientNonModifiableData() }
          ]);
          this.applyInitialFieldDefaults();
        });
      return;
    }

    if (this.assessment && this.isToday(new Date(this.assessment.createdAt))) {
      this.medicalHistoryLinkingService.populateFromSources(this.formGroup, [
        { populatorKey: 'nonModifiable', data: this.getPatientNonModifiableData() }
      ]);
    }
    this.applyInitialFieldDefaults();
  }

  private applyInitialFieldDefaults(): void {
    if (this.datePickerInputRef) {
      this.datePickerInputRef.value = new Date().toISOString();
    }
    this.disableNonModfiableControls();
  }

  private getPatientNonModifiableData(): any {
    const patient = this.patientService.patient;
    if (!patient || !patient.nonModifiableData) {
      return null;
    }
    try {
      return JSON.parse(patient.nonModifiableData);
    } catch (e) {
      return null;
    }
  }

  public updateAssessmentId() {
    const pathname = location.pathname;
    this.id = pathname.substring(pathname.indexOf(':') + 1, pathname.length - 1);
  }

  public isToday(date: Date) {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  }

  public onPatientFileModified(nonModifiableData) {
    this.medicalHistoryLinkingService.populateFromSources(this.formGroup, [
      { populatorKey: 'nonModifiable', data: nonModifiableData }
    ]);
  }

  public disableNonModfiableControls() {
    Object.values(this.derfsService.flattenedDerfsSchema).forEach(controlSchemaData => {
      this.disableControlIfNonModifiable(controlSchemaData);
    });
  }

  public disableControlIfNonModifiable(schemaData: any) {
    if (schemaData.mapConditions === undefined) {
      return;
    }
    const mapConditionsLength = schemaData.mapConditions.length;
    for (let i = 0; i < mapConditionsLength; i++) {
      if (schemaData.nonModifiableConfig && schemaData.nonModifiableConfig.nonModifiable) {
        if (this.formGroup.controls[schemaData.mapsTo[i]]) {
          // this.formGroup.controls[schemaData.mapsTo[i]].disable();
          this.disabledNonModifiableFields.add(schemaData.mapsTo[i]);
          // console.log('Disabling: ', schemaData.mapsTo[i]);
        }
      }
    }
  }

  public formatThumbLabel(value: number | null): number {
    if (!value) {
      return 0;
    }

    return Math.round(value / 10);
  }

  public closeDatePicker(event: any, dp: any, input: any, control: FormControl) {
    const formattedDate = event
      .toISOString()
      .split('-')
      .join('/')
      .substr(0, 7);
    control.setValue(formattedDate);

    input.value = formattedDate;

    dp.close();
  }

  copyMedicationToFormGroup(medication: Drug) {
    if (
      !(this.formGroup.controls.medications.value as Drug[]).find(
        currentMedication => medication.id === currentMedication.id
      )
    ) {
      this.formGroup.controls.medications.setValue([
        ...(this.formGroup.controls.medications.value || []),
        medication
      ]);
    }
  }
}
